import { Injectable } from '@angular/core';
import { environment } from '@env/environment';

declare global {
  interface Window {
    heap: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class HeapAnalyticsService {

  constructor() {
    this.initializeHeap();
  }

  // Initialize Heap Analytics
  private initializeHeap(): void {
    if (!environment.heapAppId) {
      console.log('Heap Analytics: Disabled (no App ID configured)');
      return;
    }

    // Load Heap script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://cdn.heapanalytics.com/js/heap-' + environment.heapAppId + '.js';

    document.head.appendChild(script);

    // Initialize heap object
    window.heap = window.heap || [];
    window.heap.load = window.heap.load || function(appId: string, config?: any) {
      window.heap.appid = appId;
      window.heap.config = config || {};
      const scriptElement = document.createElement('script');
      scriptElement.type = 'text/javascript';
      scriptElement.async = true;
      scriptElement.src = 'https://cdn.heapanalytics.com/js/heap-' + appId + '.js';
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode?.insertBefore(scriptElement, firstScript);
      
      const functions = ['addEventProperties', 'addUserProperties', 'clearEventProperties', 
                        'identify', 'resetIdentity', 'removeEventProperty', 'setEventProperties', 
                        'track', 'unsetEventProperty'];
      
      for (let index = 0; index < functions.length; index++) {
        const func = functions[index];
        window.heap[func] = window.heap[func] || function() {
          window.heap.push([func].concat(Array.prototype.slice.call(arguments, 0)));
        };
      }
    };

    window.heap.load(environment.heapAppId);
  }

  private isHeapAvailable(): boolean {
    if (!environment.heapAppId) {
      return false; // Heap is disabled
    }
    return typeof window !== 'undefined' && 
           window.heap && 
           typeof window.heap.track === 'function';
  }

  identify(userId: string): void {
    if (this.isHeapAvailable()) {
      window.heap.identify(userId);
    }
  }

  addUserProperties(properties: Record<string, any>): void {
    if (this.isHeapAvailable()) {
      window.heap.addUserProperties(properties);
    }
  }

  track(eventName: string, properties?: Record<string, any>): void {
    if (this.isHeapAvailable()) {
      if (properties) {
        window.heap.track(eventName, properties);
      } else {
        window.heap.track(eventName);
      }
    }
  }

  addEventProperties(properties: Record<string, any>): void {
    if (this.isHeapAvailable()) {
      window.heap.addEventProperties(properties);
    }
  }

  clearEventProperties(): void {
    if (this.isHeapAvailable()) {
      window.heap.clearEventProperties();
    }
  }
}